import { useState, useEffect, useRef } from 'react';
import { Icon } from '@/modules/Icon';
import { cn } from '@/shared/utils/cn';
import { PeopleAvatarProps } from '../types';
import { getBaseClasses, getShapeClasses, isValidUrl } from '../utils';
import {
  DEFAULT_SIZE,
  DISABLED_PEOPLE_BG,
  DISABLED_PEOPLE_BLEND,
  ICON_SIZES,
  DEFAULT_ICON,
} from '../constants';
import { AvatarStatus } from './AvatarStatus';
import { IconFontSize } from '@/shared/types/types';

/**
 * ============================================
 * PEOPLE AVATAR - ATOMIC COMPONENT
 * ============================================
 * Avatar que muestra una imagen de persona
 *
 * Características:
 * - Carga lazy de imágenes
 * - Manejo de errores con fallback a icono por defecto
 * - Timeout de carga (2 segundos)
 * - Preload de imagen para mejor performance
 *
 * @component
 * @example
 * ```tsx
 * <PeopleAvatar
 *   src="https://example.com/avatar.jpg"
 *   alt="John Doe"
 *   size="md"
 * />
 * ```
 */
export const PeopleAvatar = ({
  src,
  alt = 'Avatar',
  size = DEFAULT_SIZE,
  stroke = false,
  pill = true,
  disabled = false,
  grayscaleWhenDisabled = true,
  className,
  status,
}: PeopleAvatarProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleImageError = () => {
    setHasError(true);
    setIsLoaded(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!src || !isValidUrl(src)) {
      setHasError(true);
      setIsLoaded(false);
      return;
    }

    setHasError(false);
    setIsLoaded(false);

    // Timeout de 2 segundos para mostrar fallback si tarda mucho
    timeoutRef.current = setTimeout(() => {
      setHasError(true);
    }, 2000);

    // Preload de la imagen
    const img = new Image();
    img.onload = () => {
      setIsLoaded(true);
      setHasError(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    img.onerror = () => {
      setHasError(true);
      setIsLoaded(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    img.src = src;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [src]);

  const shouldRenderImage = Boolean(src && !hasError && isLoaded);

  // Mapear tamaño del avatar al tamaño del status (solo si no se especifica en status)
  const statusSizeMap: Record<string, 'sm' | 'md' | 'lg'> = {
    xxs: 'sm',
    xs: 'sm',
    sm: 'sm',
    md: 'md',
    lg: 'md',
    xl: 'lg',
  };
  // Usar el size del status si está especificado, sino usar el mapeo automático
  const statusSize = status?.size || statusSizeMap[size] || 'md';

  if (!shouldRenderImage) {
    const fallbackClasses = cn(
      getBaseClasses(size, stroke, pill, disabled, grayscaleWhenDisabled, className, 'people'),
      disabled ? DISABLED_PEOPLE_BG : 'ui-bg-surface-disabled',
      'ui-text-disabled'
    );
    const iconSize = ICON_SIZES[size];

    return (
      <div className="ui-imj ui-relative ui-inline-flex">
        <div className={fallbackClasses}>
          <Icon name={DEFAULT_ICON} size={iconSize as IconFontSize} color="current" />
        </div>
        {status && <AvatarStatus {...status} size={statusSize} />}
      </div>
    );
  }

  const baseClasses = cn(
    getBaseClasses(size, stroke, pill, disabled, grayscaleWhenDisabled, className, 'people'),
    disabled && DISABLED_PEOPLE_BG
  );

  return (
    <div className="ui-imj ui-relative ui-inline-flex">
      <div className={baseClasses}>
        <img
          src={src}
          alt={alt}
          className={cn(
            'ui-h-full ui-w-full ui-object-cover',
            getShapeClasses(pill, size), // Aplicar border-radius también a la imagen según tamaño
            !pill && 'ui-overflow-hidden',
            disabled && grayscaleWhenDisabled && DISABLED_PEOPLE_BLEND
          )}
          onError={handleImageError}
          onLoad={handleImageLoad}
          loading="lazy"
        />
      </div>
      {status && <AvatarStatus {...status} size={statusSize} />}
    </div>
  );
};
